import { Link, Outlet, useLocation } from 'react-router-dom';
import '@universityofmaryland/utilityheader';
import { useEffect, useState } from "react";
import {Menu, Settings, Wrench} from "lucide-react";
import { OrganizationSelector, WorkspaceSelector } from '@umd-dit/workspaces-component-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import AdminSettingsDialog from './admin/AdminSettingsDialog';

export default function Layout() {
  const location = useLocation();
  const { 
    isAdmin, 
    selectedOrganizationId, 
    selectOrganization,
    selectedWorkspaceId,
    selectWorkspace,
    isAuthenticated
  } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTwoRowLayout, setIsTwoRowLayout] = useState(false);
  const [showAdminSettings, setShowAdminSettings] = useState(false);

  useEffect(() => {
    // Function to check if we're on a mobile device and if we need two-row layout
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 640);
      setIsTwoRowLayout(window.innerWidth < 1235 && window.innerWidth >= 640);
    };

    // Check initially
    checkScreenSize();

    // Add event listener for window resize
    window.addEventListener('resize', checkScreenSize);

    // Clean up
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    // Update the auth component's class when mobile menu state changes
    const authElement = document.querySelector('umd-auth');
    if (authElement) {
      if (mobileMenuOpen) {
        authElement.classList.add('menu-open');
      } else {
        authElement.classList.remove('menu-open');
      }
    }
  }, [mobileMenuOpen]);

  const isActive = (path: string) => {
    return location.pathname === path ? 'bg-gray-200' : '';
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <umd-utility-header
        size="100%"
        padding="20px"
      ></umd-utility-header>
        <>
          <nav className="bg-white shadow-sm">
            <div className="container mx-auto px-4">
              <div className="flex h-16 items-center justify-between">
                {isMobile ? (
                  // Mobile view
                  <button
                    type="button"
                    className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:bg-gray-100"
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  >
                    <Menu className="h-6 w-6" />
                  </button>
                ) : isTwoRowLayout ? (
                  // Two-row layout (width < 1235px but not mobile)
                  <div className="flex items-center space-x-2">
                    {/* Only show organization selector for global admins */}
                    {isAuthenticated && isAdmin && (
                      <>
                        {console.log('[Layout] Rendering OrganizationSelector with API URL:', import.meta.env.VITE_WORKSPACES_API_URL)}
                        <OrganizationSelector
                          appId="legislative-review"
                          workspacesApiUrl={import.meta.env.VITE_WORKSPACES_API_URL}
                          selectedOrganizationId={selectedOrganizationId || undefined}
                          onOrganizationSelect={selectOrganization}
                          debug={import.meta.env.VITE_WORKSPACES_DEBUG_LOGGING === 'true'}
                        />
                      </>
                    )}
                    {/* Only show workspace switcher when authenticated */}
                    {isAuthenticated && (() => {
                      const apiUrl = import.meta.env.VITE_WORKSPACES_API_URL;
                      console.log('[Layout TwoRow] WorkspaceSelector - About to pass API URL:', apiUrl);
                      console.log('[Layout TwoRow] WorkspaceSelector - Type of API URL:', typeof apiUrl);
                      console.log('[Layout TwoRow] WorkspaceSelector - Is API URL defined?', apiUrl !== undefined);
                      return (
                        <WorkspaceSelector
                          appId="legislative-review"
                          workspacesApiUrl={apiUrl}
                          selectedOrganizationId={selectedOrganizationId || undefined}
                          selectedWorkspaceId={selectedWorkspaceId || undefined}
                          onWorkspaceSelect={selectWorkspace}
                          refreshOnSelect={false}
                          debug={import.meta.env.VITE_WORKSPACES_DEBUG_LOGGING === 'true'}
                        />
                      );
                    })()}
                  </div>
                ) : (
                  // Full desktop view
                  <div className="flex items-center space-x-8">
                    {/* Only show organization selector for global admins */}
                    {isAuthenticated && isAdmin && (
                      <>
                        {console.log('[Layout] Rendering OrganizationSelector with API URL:', import.meta.env.VITE_WORKSPACES_API_URL)}
                        <OrganizationSelector
                          appId="legislative-review"
                          workspacesApiUrl={import.meta.env.VITE_WORKSPACES_API_URL}
                          selectedOrganizationId={selectedOrganizationId || undefined}
                          onOrganizationSelect={selectOrganization}
                          debug={import.meta.env.VITE_WORKSPACES_DEBUG_LOGGING === 'true'}
                        />
                      </>
                    )}
                    {/* Only show workspace switcher when authenticated */}
                    {isAuthenticated && (() => {
                      const apiUrl = import.meta.env.VITE_WORKSPACES_API_URL;
                      console.log('[Layout Desktop] WorkspaceSelector - About to pass API URL:', apiUrl);
                      console.log('[Layout Desktop] WorkspaceSelector - Type of API URL:', typeof apiUrl);
                      console.log('[Layout Desktop] WorkspaceSelector - Is API URL defined?', apiUrl !== undefined);
                      return (
                        <WorkspaceSelector
                          appId="legislative-review"
                          workspacesApiUrl={apiUrl}
                          selectedOrganizationId={selectedOrganizationId || undefined}
                          selectedWorkspaceId={selectedWorkspaceId || undefined}
                          onWorkspaceSelect={selectWorkspace}
                          refreshOnSelect={false}
                          debug={import.meta.env.VITE_WORKSPACES_DEBUG_LOGGING === 'true'}
                        />
                      );
                    })()}
                    <Link
                      to="/bills"
                      className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md ${isActive('/bills')}`}
                    >
                      Bills
                    </Link>
                    <Link
                      to="/jobs"
                      className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md ${isActive('/jobs')}`}
                    >
                      Jobs
                    </Link>
                    <Link
                      to="/costs"
                      className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md ${isActive('/costs')}`}
                    >
                      Costs
                    </Link>
                    <Link
                      to="/config"
                      className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md ${isActive('/config')}`}
                    >
                      Config
                    </Link>

                    {/* Admin link and settings button */}
                    {isAdmin && (
                      <>
                        <Link
                          to="/admin/management"
                          className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md ${isActive('/admin/management')}`}
                        >
                          <Settings className="h-4 w-4 mr-1" />
                          Admin
                        </Link>
                        <Link
                          to="/admin/datasource"
                          className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md ${isActive('/admin/datasource')}`}
                        >
                          Datasources
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowAdminSettings(true)}
                          className="inline-flex items-center px-3 py-2 text-sm font-medium"
                        >
                          <Wrench className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Two-row layout - second row for navigation links */}
            {isTwoRowLayout && (
              <div className="container mx-auto px-4 py-2 flex items-center space-x-8 overflow-x-auto">
                <Link
                  to="/bills"
                  className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md ${isActive('/bills')}`}
                >
                  Bills
                </Link>
                <Link
                  to="/jobs"
                  className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md ${isActive('/jobs')}`}
                >
                  Jobs
                </Link>
                <Link
                  to="/costs"
                  className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md ${isActive('/costs')}`}
                >
                  Costs
                </Link>
                <Link
                  to="/config"
                  className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md ${isActive('/config')}`}
                >
                  Config
                </Link>
                {isAdmin && (
                  <>
                    <Link
                      to="/admin/management"
                      className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md ${isActive('/admin/management')}`}
                    >
                      <Settings className="h-4 w-4 mr-1" />
                      Admin
                    </Link>
                    <Link
                      to="/admin/datasource"
                      className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md ${isActive('/admin/datasource')}`}
                    >
                      Datasources
                    </Link>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowAdminSettings(true)}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium"
                    >
                      <Wrench className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            )}

            {/* Mobile menu dropdown */}
            {isMobile && mobileMenuOpen && (
              <div className="pt-2 pb-3 space-y-1">
                <Link
                  to="/bills"
                  className={`block px-3 py-2 text-base font-medium ${isActive('/bills')}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Bills
                </Link>
                <Link
                  to="/jobs"
                  className={`block px-3 py-2 text-base font-medium ${isActive('/jobs')}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Jobs
                </Link>
                <Link
                  to="/costs"
                  className={`block px-3 py-2 text-base font-medium ${isActive('/costs')}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Costs
                </Link>
                <Link
                  to="/config"
                  className={`block px-3 py-2 text-base font-medium ${isActive('/config')}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Config
                </Link>
                {isAdmin && (
                  <>
                    <Link
                      to="/admin/management"
                      className={`block px-3 py-2 text-base font-medium ${isActive('/admin/management')}`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Settings className="h-4 w-4 inline mr-1" />
                      Admin
                    </Link>
                    <Link
                      to="/admin/datasource"
                      className={`block px-3 py-2 text-base font-medium ${isActive('/admin/datasource')}`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Datasources
                    </Link>
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        setShowAdminSettings(true);
                      }}
                      className="block w-full text-left px-3 py-2 text-base font-medium"
                    >
                      <Wrench className="h-4 w-4 inline mr-1" />
                      Admin Settings
                    </button>
                  </>
                )}
              </div>
            )}
          </nav>

          <main className="py-6">
            <div className="container mx-auto px-4">
              <Outlet />
            </div>
          </main>
        </>
        
      {/* Admin Settings Dialog */}
      {isAdmin && (
        <AdminSettingsDialog 
          open={showAdminSettings} 
          onOpenChange={setShowAdminSettings} 
        />
      )}
    </div>
  );
}
